import {
  keepPreviousData,
  QueryFunctionContext,
  useInfiniteQuery,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  APIMutationOptions,
  APIMutationResult,
  useAPIMutation,
} from 'shared/api/APIMutation';
import {
  APIPageQueryOptions,
  APIPageQueryResult,
  useAPIPageQuery,
} from 'shared/api/APIPageQuery';
import {
  APIQueryOptions,
  APIQueryResult,
  useAPIQuery,
} from 'shared/api/APIQuery';
import {
  APIPageResponse,
  requestCarrierAPI,
} from 'shared/api/CarrierAPIClient';
import {
  requestJSONReportingAPI,
  requestReportingAPI,
} from 'shared/api/ReportingAPIClient';
import { castAPIData } from 'shared/api/APISchema';
import { useShowSubscriptionCancelledDialog } from 'shared/errors/data/useShowSubscriptionCancelledDialog';
import {
  accountsPayableSchema,
  customReportSchema,
  DashboardDataDTO,
  DashboardDataFilters,
  dashboardDataSchema,
  DashboardFilterDTO,
  DashboardFilterOptionsMap,
  dashboardFilterOptionsSchema,
  DashboardWidgetData,
  DashboardDetailDTO,
  dashboardDetailSchema,
  DashboardListItemDTO,
  dashboardListItemSchema,
  driverPaySchema,
  DriverPayTotalReportDTO,
  driverPayTotalReportSchema,
  ReportsAccountsPayableDTO,
  ReportsAccountsReceivableDTO,
  reportsAccountsReceivableSchema,
  ReportsCompanyRevenueDTO,
  ReportsCompanyRevenueParamsDTO,
  reportsCompanyRevenueSchema,
  ReportsCustomDTO,
  ReportsDriverPayDTO,
  ReportsDriverPayParamsDTO,
  ReportsDriverPayQueryParamsDTO,
  ReportsPeriodDTO,
  SendDriverPayReportRequestDTO,
  SendRevenueReportRequestDTO,
  DASHBOARD_WIDGET_KEYS,
  DASHBOARD_KEYS,
  WIDGET_KINDS,
} from './ReportsDTO';
import { ONE_HOUR } from 'shared/constants/NumberConstants';
import { z } from 'zod';
import { compact } from 'es-toolkit';

export class ReportsAPI {
  updateDriversPayRate(guid: string, payRate: number) {
    return requestCarrierAPI(
      ['PATCH /internal/web/reports/driver-pay/{guid}/rate/', { guid }],
      {
        json: { pay_rate: payRate },
      },
    );
  }
}

export function useReportsAPI() {
  return useMemo(
    () => ({
      sendDriverPayReport: (values: SendDriverPayReportRequestDTO) =>
        requestCarrierAPI(
          'POST /internal/web/reports/driver-pay/email-report/',
          { json: values },
        ),

      sendRevenueReport: (
        widgetId: string,
        values: SendRevenueReportRequestDTO,
      ) =>
        requestReportingAPI(
          ['POST /api/v1/carrier/widgets/{widgetId}/send-report', { widgetId }],
          { json: values },
        ),

      sendCustomReport: (values: ReportsCustomDTO) =>
        requestCarrierAPI('POST /internal/web/reports/custom-report/', {
          json: values,
        }),

      updateCustomReport: (values: ReportsCustomDTO) =>
        requestCarrierAPI(
          [
            'PUT /internal/web/reports/saved-reports/{guid}/',
            { guid: values.guid },
          ],
          { json: values },
        ),

      createCustomReport: ({ guid, ...values }: ReportsCustomDTO) =>
        requestCarrierAPI('POST /internal/web/reports/saved-reports/', {
          json: values,
        }),
    }),
    [],
  );
}

export function useAccountsPayableReports(
  params: ReportsPeriodDTO,
  options?: APIQueryOptions<ReportsAccountsPayableDTO>,
): APIQueryResult<ReportsAccountsPayableDTO> {
  return useAPIQuery(
    ['reports', 'accountsPayable', { params }],
    () =>
      requestCarrierAPI([
        'GET /internal/web/reports/accounts-payable/{?params*}',
        { params },
      ]),
    { ...options, schema: accountsPayableSchema },
  );
}

export function useDriverPayTotal(
  params: ReportsDriverPayQueryParamsDTO,
  options?: APIQueryOptions<DriverPayTotalReportDTO>,
): APIQueryResult<DriverPayTotalReportDTO> {
  return useAPIQuery(
    ['reports', 'driverPayTotal', { params }],
    () =>
      requestCarrierAPI([
        'GET /internal/web/reports/driver-pay/total/{?params*}',
        { params },
      ]),
    { ...options, schema: driverPayTotalReportSchema },
  );
}

export function useDriverPayReport(
  params: ReportsDriverPayParamsDTO,
  options?: APIPageQueryOptions<ReportsDriverPayDTO>,
): APIPageQueryResult<ReportsDriverPayDTO> {
  return useAPIPageQuery(
    ['reports', 'driver-pay-report', { params }],
    () =>
      requestCarrierAPI([
        'GET /internal/web/reports/driver-pay/{?params*}',
        { params },
      ]),
    { ...options, schema: driverPaySchema },
  );
}

interface SavedReportsListParams {
  page?: number;
  page_size?: number;
}

export function useSavedReportsList(
  { page = 1, page_size = 20 }: SavedReportsListParams = {
    page: 1,
    page_size: 20,
  },
) {
  return useInfiniteQuery({
    queryKey: ['custom-report', 'saved-reports-list', { page, page_size }],
    queryFn: ({
      pageParam,
    }: QueryFunctionContext<
      readonly [string, string, { page: number; page_size: number }],
      number
    >) => {
      return requestCarrierAPI([
        'GET /internal/web/reports/saved-reports/{?params*}',
        { params: { page: pageParam, page_size } },
      ]);
    },

    getNextPageParam: (
      lastPage: APIPageResponse<ReportsCustomDTO>,
      allPages: Array<APIPageResponse<ReportsCustomDTO>>,
    ) => {
      const maxPage = Math.ceil(lastPage.pagination.count / (page_size || 20));
      if (allPages.length === maxPage) {
        return undefined;
      }

      return allPages.length + 1;
    },
    initialPageParam: 1,
  });
}

export function useDeleteSavedReport(
  options?: APIMutationOptions<string>,
): APIMutationResult<string> {
  const queryClient = useQueryClient();

  return useAPIMutation(
    (guid) =>
      requestCarrierAPI([
        'DELETE /internal/web/reports/saved-reports/{guid}/',
        { guid },
      ]).then((response) => {
        void queryClient.invalidateQueries({
          queryKey: ['custom-report', 'saved-reports-list'],
        });
        return response;
      }),
    options,
  );
}

export function useSavedReportItem(
  guid: string,
): APIQueryResult<ReportsCustomDTO> {
  return useAPIQuery(
    ['custom-report', 'saved-reports-item', { guid }],
    () =>
      requestCarrierAPI([
        'GET /internal/web/reports/saved-reports/{guid}/',
        { guid },
      ]),
    { schema: customReportSchema, enabled: !!guid },
  );
}

export function useAccountsReceivableReports(
  params: ReportsPeriodDTO,
): APIQueryResult<ReportsAccountsReceivableDTO> {
  return useAPIQuery(
    ['reports', 'accountsReceivable', { params }],
    () =>
      requestCarrierAPI([
        'GET /internal/web/reports/accounts-receivable/{?params*}',
        { params },
      ]),
    { schema: reportsAccountsReceivableSchema },
  );
}

export function useCompanyRevenueReports(
  params: ReportsCompanyRevenueParamsDTO,
): APIQueryResult<ReportsCompanyRevenueDTO> {
  return useAPIQuery(
    ['reports', 'companyRevenue', { params }],
    () =>
      requestCarrierAPI([
        'GET /internal/web/reports/company-revenue/{?params*}',
        { params },
      ]),
    { schema: reportsCompanyRevenueSchema },
  );
}

const DASHBOARDS_CACHE_TIME = ONE_HOUR * 4;

const dashboardListSchema = z.array(dashboardListItemSchema).default([]);

export function useCarrierDashboards(
  options?: APIQueryOptions<DashboardListItemDTO[]>,
): APIQueryResult<DashboardListItemDTO[]> {
  const { isOpenSubscriptionCancelledDialog } =
    useShowSubscriptionCancelledDialog();

  const query: APIQueryResult<DashboardListItemDTO[]> = useAPIQuery(
    ['reporting', 'dashboards'],
    () => requestJSONReportingAPI('GET /api/v1/carrier/dashboards'),
    {
      ...options,
      staleTime: DASHBOARDS_CACHE_TIME,
      gcTime: DASHBOARDS_CACHE_TIME,
      enabled: !isOpenSubscriptionCancelledDialog && (options?.enabled ?? true),
      schema: dashboardListSchema,
    },
  );

  if (isOpenSubscriptionCancelledDialog) {
    return {
      ...query,
      data: undefined,
      isLoading: true,
      isFetching: true,
    } as APIQueryResult<DashboardListItemDTO[]>;
  }

  return query;
}

export function useCarrierDashboard(
  dashboardId: string | undefined,
  options?: APIQueryOptions<DashboardDetailDTO>,
): APIQueryResult<DashboardDetailDTO> {
  return useAPIQuery(
    ['reporting', 'dashboard', { dashboardId }],
    () =>
      requestJSONReportingAPI([
        'GET /api/v1/carrier/dashboards/{dashboardId}',
        { dashboardId: dashboardId ?? '' },
      ]),
    {
      ...options,
      staleTime: DASHBOARDS_CACHE_TIME,
      gcTime: DASHBOARDS_CACHE_TIME,
      enabled: !!dashboardId,
      schema: dashboardDetailSchema,
    },
  );
}

export function useCarrierDashboardData(
  dashboardId: string | undefined,
  filters: DashboardDataFilters,
  widgetIds?: string[],
  options?: APIQueryOptions<DashboardDataDTO> & { sort?: string | null },
): APIQueryResult<DashboardDataDTO> {
  const { sort, ...queryOptions } = options ?? {};
  const isEnabled =
    !!dashboardId &&
    !!widgetIds &&
    widgetIds.length > 0 &&
    !!filters.date_range?.end_date &&
    !!filters.date_range?.start_date;

  return useAPIQuery(
    ['reporting', 'dashboard-data', { dashboardId, widgetIds, filters, sort }],
    () =>
      requestJSONReportingAPI(
        [
          'POST /api/v1/carrier/dashboards/{dashboardId}/data',
          { dashboardId: dashboardId ?? '' },
        ],
        {
          json: {
            filters,
            widget_ids: widgetIds ?? [],
            ...(sort && widgetIds && widgetIds.length > 0
              ? {
                  widget_sorts: Object.fromEntries(
                    widgetIds.map((id) => [id, sort]),
                  ),
                }
              : {}),
          },
        },
      ),
    {
      staleTime: DASHBOARDS_CACHE_TIME,
      gcTime: DASHBOARDS_CACHE_TIME,
      enabled: isEnabled,
      keepPreviousData: true,
      schema: dashboardDataSchema,
      ...queryOptions,
    },
  );
}

export function useCarrierDashboardFilterOptions(
  dashboardId: string | undefined,
  options?: APIQueryOptions<DashboardFilterOptionsMap>,
): APIQueryResult<DashboardFilterOptionsMap> {
  return useAPIQuery<DashboardFilterOptionsMap>(
    ['reporting', 'dashboard-filter-options', { dashboardId }],
    () =>
      requestJSONReportingAPI([
        'GET /api/v1/carrier/dashboards/{dashboardId}/filter-options',
        { dashboardId: dashboardId ?? '' },
      ]),
    {
      ...options,
      staleTime: DASHBOARDS_CACHE_TIME,
      gcTime: DASHBOARDS_CACHE_TIME,
      enabled: !!dashboardId,
      schema: dashboardFilterOptionsSchema,
    },
  );
}

export interface UseRevenueFiltersResult {
  filters: DashboardFilterDTO[];
  options: DashboardFilterOptionsMap;
  isFiltersLoading: boolean;
  isOptionsLoading: boolean;
}

export function useDashboardFilters(
  dashboardKey: string,
): UseRevenueFiltersResult {
  const dashboardsQuery = useCarrierDashboards();
  const dashboardId = dashboardsQuery.data?.find(
    (dashboard) => dashboard.key === dashboardKey,
  )?.id;

  const dashboardQuery = useCarrierDashboard(dashboardId);
  const optionsQuery = useCarrierDashboardFilterOptions(dashboardId);

  return {
    filters: dashboardQuery.data?.filters ?? [],
    options: optionsQuery.data ?? {},
    isFiltersLoading: dashboardsQuery.isLoading || dashboardQuery.isLoading,
    isOptionsLoading: optionsQuery.isLoading,
  };
}

export interface UseExpensesFiltersResult {
  filters: DashboardFilterDTO[];
  options: DashboardFilterOptionsMap;
  isFiltersLoading: boolean;
  isOptionsLoading: boolean;
}

export function useExpensesFilters(): UseExpensesFiltersResult {
  const dashboardsQuery = useCarrierDashboards();
  const dashboardId = dashboardsQuery.data?.find(
    (dashboard) => dashboard.key === DASHBOARD_KEYS.EXPENSES,
  )?.id;

  const dashboardQuery = useCarrierDashboard(dashboardId);
  const optionsQuery = useCarrierDashboardFilterOptions(dashboardId);

  return {
    filters: dashboardQuery.data?.filters ?? [],
    options: optionsQuery.data ?? {},
    isFiltersLoading: dashboardsQuery.isLoading || dashboardQuery.isLoading,
    isOptionsLoading: optionsQuery.isLoading,
  };
}

export function useRevenueFilters(): UseRevenueFiltersResult {
  return useDashboardFilters(DASHBOARD_KEYS.REVENUE);
}

export function useInvoicesFilters(): UseRevenueFiltersResult {
  return useDashboardFilters(DASHBOARD_KEYS.INVOICES);
}

export function useDeliveryTrendFilters(): UseRevenueFiltersResult {
  return useDashboardFilters(DASHBOARD_KEYS.DELIVERY_TREND);
}

interface DashboardWidgetTabQueriesOptions {
  activeTabKey?: string;
  prefetchAllTabs?: boolean;
}

export function useDashboardWidgetTabQueries(
  dashboardId: string | undefined,
  widgetId: string | undefined,
  tabKeys: string[],
  filters: DashboardDataFilters,
  {
    activeTabKey,
    prefetchAllTabs = false,
  }: DashboardWidgetTabQueriesOptions = {},
) {
  const isEnabled =
    !!dashboardId &&
    !!widgetId &&
    !!filters.date_range?.start_date &&
    !!filters.date_range?.end_date;

  return useQueries({
    queries: tabKeys.map((tabKey) => ({
      queryKey: [
        'reporting',
        'dashboard-data',
        { dashboardId, widgetId, tab: tabKey, filters },
      ],
      queryFn: () =>
        requestJSONReportingAPI(
          [
            'POST /api/v1/carrier/dashboards/{dashboardId}/data',
            { dashboardId: dashboardId ?? '' },
          ],
          {
            json: {
              filters,
              widget_ids: widgetId ? [widgetId] : [],
              widget_views: widgetId ? { [widgetId]: tabKey } : {},
            },
          },
        ).then(({ data }) => castAPIData(dashboardDataSchema, data)),
      enabled: isEnabled && (prefetchAllTabs || tabKey === activeTabKey),
      staleTime: DASHBOARDS_CACHE_TIME,
      gcTime: DASHBOARDS_CACHE_TIME,
      placeholderData: keepPreviousData,
    })),
  });
}

export interface UseReportsDashboardResult {
  dashboardId?: string;
  dashboard?: DashboardDetailDTO;
  isLoading: boolean;
}

export function useReportsDashboard(
  dashboardKey: string = DASHBOARD_KEYS.OVERVIEW,
): UseReportsDashboardResult {
  const dashboardsQuery = useCarrierDashboards();
  const dashboardId = dashboardsQuery.data?.find(
    (dashboard) => dashboard.key === dashboardKey,
  )?.id;

  const dashboardQuery = useCarrierDashboard(dashboardId);

  return {
    dashboardId,
    dashboard: dashboardQuery.data,
    isLoading: dashboardsQuery.isLoading || dashboardQuery.isLoading,
  };
}

export interface UseExpensesWidgetResult {
  widgetData?: DashboardWidgetData;
  lastUpdated?: string | null;
  isLoading: boolean;
  isWidgetExist: boolean;
}

export function useExpensesWidget(
  widgetKey: string,
  filters: DashboardDataFilters,
): UseExpensesWidgetResult {
  const {
    dashboardId,
    dashboard,
    isLoading: isDashboardLoading,
  } = useReportsDashboard(DASHBOARD_KEYS.EXPENSES);

  const widget = dashboard?.widgets?.find((item) => item.key === widgetKey);
  const widgetIds = compact([widget?.id]);

  const dashboardDataQuery = useCarrierDashboardData(
    dashboardId,
    filters,
    widgetIds,
  );

  const widgetData = useMemo(
    () =>
      dashboardDataQuery.data?.widgets?.find((item) => item.id === widget?.id),
    [dashboardDataQuery.data, widget?.id],
  );

  const isLoading =
    isDashboardLoading ||
    dashboardDataQuery.isLoading ||
    dashboardDataQuery.isFetching;

  return {
    widgetData,
    lastUpdated: dashboardDataQuery.data?.updated_at,
    isLoading,
    isWidgetExist: !!widget,
  };
}

export interface UseRevenueLoadDetailsResult {
  widgetData?: DashboardWidgetData;
  isLoading: boolean;
  isFetching: boolean;
  isWidgetExist: boolean;
  sortableColumns: string[];
}

export function useRevenueLoadDetails(
  filters: DashboardDataFilters,
  sort?: string | null,
): UseRevenueLoadDetailsResult {
  const {
    dashboardId,
    dashboard,
    isLoading: isDashboardLoading,
  } = useReportsDashboard(DASHBOARD_KEYS.REVENUE);

  const widget = dashboard?.widgets?.find(
    (item) => item.key === DASHBOARD_WIDGET_KEYS.REVENUE_LOAD_DETAILS,
  );
  const widgetIds = compact([widget?.id]);

  const dashboardDataQuery = useCarrierDashboardData(
    dashboardId,
    filters,
    widgetIds,
    { sort, staleTime: 0 },
  );

  const widgetData = useMemo(
    () =>
      dashboardDataQuery.data?.widgets?.find((item) => item.id === widget?.id),
    [dashboardDataQuery.data, widget?.id],
  );

  const isLoading = isDashboardLoading || dashboardDataQuery.isLoading;

  return {
    widgetData,
    isLoading,
    isFetching: dashboardDataQuery.isFetching,
    isWidgetExist: !!widget,
    sortableColumns: widget?.sortable_columns ?? [],
  };
}

export interface UseInvoicesDetailsResult {
  widgetData?: DashboardWidgetData;
  isLoading: boolean;
  isFetching: boolean;
  isWidgetExist: boolean;
  sortableColumns: string[];
}

export function useInvoicesDetails(
  filters: DashboardDataFilters,
  sort?: string | null,
): UseInvoicesDetailsResult {
  const {
    dashboardId,
    dashboard,
    isLoading: isDashboardLoading,
  } = useReportsDashboard(DASHBOARD_KEYS.INVOICES);

  const widget = dashboard?.widgets?.find(
    (item) => item.key === DASHBOARD_WIDGET_KEYS.INVOICE_DETAILS,
  );
  const widgetIds = compact([widget?.id]);

  const dashboardDataQuery = useCarrierDashboardData(
    dashboardId,
    filters,
    widgetIds,
    { sort, staleTime: 0 },
  );

  const widgetData = useMemo(
    () =>
      dashboardDataQuery.data?.widgets?.find((item) => item.id === widget?.id),
    [dashboardDataQuery.data, widget?.id],
  );

  const isLoading = isDashboardLoading || dashboardDataQuery.isLoading;

  return {
    widgetData,
    isLoading,
    isFetching: dashboardDataQuery.isFetching,
    isWidgetExist: !!widget,
    sortableColumns: widget?.sortable_columns ?? [],
  };
}

export interface UseExpensesLevelDetailsResult {
  widgetData?: DashboardWidgetData;
  isLoading: boolean;
  isFetching: boolean;
  isWidgetExist: boolean;
  sortableColumns: string[];
}

export function useExpensesLevelDetails(
  filters: DashboardDataFilters,
  sort?: string | null,
): UseExpensesLevelDetailsResult {
  const {
    dashboardId,
    dashboard,
    isLoading: isDashboardLoading,
  } = useReportsDashboard(DASHBOARD_KEYS.EXPENSES);

  const widget = dashboard?.widgets?.find(
    (item) => item.key === DASHBOARD_WIDGET_KEYS.EXPENSE_TABLE,
  );
  const widgetIds = compact([widget?.id]);

  const dashboardDataQuery = useCarrierDashboardData(
    dashboardId,
    filters,
    widgetIds,
    { sort, staleTime: 0 },
  );

  const widgetData = useMemo(
    () =>
      dashboardDataQuery.data?.widgets?.find((item) => item.id === widget?.id),
    [dashboardDataQuery.data, widget?.id],
  );

  const isLoading = isDashboardLoading || dashboardDataQuery.isLoading;

  return {
    widgetData,
    isLoading,
    isFetching: dashboardDataQuery.isFetching,
    isWidgetExist: !!widget,
    sortableColumns: widget?.sortable_columns ?? [],
  };
}

export interface UseDeliveryLoadDetailsResult {
  widgetData?: DashboardWidgetData;
  isLoading: boolean;
  isFetching: boolean;
  isWidgetExist: boolean;
  sortableColumns: string[];
}

export function useDeliveryLoadDetails(
  filters: DashboardDataFilters,
  sort?: string | null,
): UseDeliveryLoadDetailsResult {
  const {
    dashboardId,
    dashboard,
    isLoading: isDashboardLoading,
  } = useReportsDashboard(DASHBOARD_KEYS.DELIVERY_TREND);

  const widget = dashboard?.widgets?.find(
    (item) => item.key === DASHBOARD_WIDGET_KEYS.DELIVERY_LOAD_DETAILS,
  );
  const widgetIds = compact([widget?.id]);

  const dashboardDataQuery = useCarrierDashboardData(
    dashboardId,
    filters,
    widgetIds,
    { sort, staleTime: 0 },
  );

  const widgetData = useMemo(
    () =>
      dashboardDataQuery.data?.widgets?.find((item) => item.id === widget?.id),
    [dashboardDataQuery.data, widget?.id],
  );

  const isLoading = isDashboardLoading || dashboardDataQuery.isLoading;

  return {
    widgetData,
    isLoading,
    isFetching: dashboardDataQuery.isFetching,
    isWidgetExist: !!widget,
    sortableColumns: widget?.sortable_columns ?? [],
  };
}

export interface UseReportsRevenueKpisResult {
  kpisData?: DashboardWidgetData;
  lastUpdated?: string | null;
  isLoading: boolean;
  isWidgetExist: boolean;
}

export function useReportsRevenueKpis(
  filters: DashboardDataFilters,
): UseReportsRevenueKpisResult {
  const dashboardsQuery = useCarrierDashboards();
  const dashboardId = dashboardsQuery.data?.find(
    (dashboard) => dashboard.key === DASHBOARD_KEYS.REVENUE,
  )?.id;

  const dashboardQuery = useCarrierDashboard(dashboardId);

  const kpisWidget = dashboardQuery.data?.widgets?.find(
    (widget) => widget.key === DASHBOARD_WIDGET_KEYS.REVENUE_KPIS,
  );

  const widgetIds = compact([kpisWidget?.id]);

  const dashboardDataQuery = useCarrierDashboardData(
    dashboardId,
    filters,
    widgetIds,
  );

  const kpisData = useMemo(
    () =>
      dashboardDataQuery.data?.widgets?.find(
        (widget) => widget.id === kpisWidget?.id,
      ),
    [dashboardDataQuery.data, kpisWidget?.id],
  );

  const isLoading =
    dashboardsQuery.isLoading ||
    dashboardQuery.isLoading ||
    dashboardDataQuery.isLoading ||
    dashboardDataQuery.isFetching;

  return {
    kpisData,
    lastUpdated: dashboardDataQuery.data?.updated_at,
    isLoading,
    isWidgetExist: !!kpisWidget,
  };
}

export interface UseReportsInvoicesKpisResult {
  kpisData?: DashboardWidgetData;
  lastUpdated?: string | null;
  isLoading: boolean;
  isWidgetExist: boolean;
}

export function useReportsInvoicesKpis(
  filters: DashboardDataFilters,
): UseReportsInvoicesKpisResult {
  const dashboardsQuery = useCarrierDashboards();
  const dashboardId = dashboardsQuery.data?.find(
    (dashboard) => dashboard.key === DASHBOARD_KEYS.INVOICES,
  )?.id;

  const dashboardQuery = useCarrierDashboard(dashboardId);

  const kpisWidget = dashboardQuery.data?.widgets?.find(
    (widget) => widget.key === DASHBOARD_WIDGET_KEYS.INVOICE_KPIS,
  );

  const widgetIds = compact([kpisWidget?.id]);

  const dashboardDataQuery = useCarrierDashboardData(
    dashboardId,
    filters,
    widgetIds,
  );

  const kpisData = useMemo(
    () =>
      dashboardDataQuery.data?.widgets?.find(
        (widget) => widget.id === kpisWidget?.id,
      ),
    [dashboardDataQuery.data, kpisWidget?.id],
  );

  const isLoading =
    dashboardsQuery.isLoading ||
    dashboardQuery.isLoading ||
    dashboardDataQuery.isLoading ||
    dashboardDataQuery.isFetching;

  return {
    kpisData,
    lastUpdated: dashboardDataQuery.data?.updated_at,
    isLoading,
    isWidgetExist: !!kpisWidget,
  };
}

export interface UseReportsDeliveryKpisResult {
  kpisData?: DashboardWidgetData;
  lastUpdated?: string | null;
  isLoading: boolean;
  isWidgetExist: boolean;
}

export function useReportsDeliveryKpis(
  filters: DashboardDataFilters,
): UseReportsDeliveryKpisResult {
  const dashboardsQuery = useCarrierDashboards();
  const dashboardId = dashboardsQuery.data?.find(
    (dashboard) => dashboard.key === DASHBOARD_KEYS.DELIVERY_TREND,
  )?.id;

  const dashboardQuery = useCarrierDashboard(dashboardId);

  const kpisWidget = dashboardQuery.data?.widgets?.find(
    (widget) => widget.key === DASHBOARD_WIDGET_KEYS.CARRIER_DELIVERY_KPIS,
  );

  const widgetIds = compact([kpisWidget?.id]);

  const dashboardDataQuery = useCarrierDashboardData(
    dashboardId,
    filters,
    widgetIds,
  );

  const kpisData = useMemo(
    () =>
      dashboardDataQuery.data?.widgets?.find(
        (widget) => widget.id === kpisWidget?.id,
      ),
    [dashboardDataQuery.data, kpisWidget?.id],
  );

  const isLoading =
    dashboardsQuery.isLoading ||
    dashboardQuery.isLoading ||
    dashboardDataQuery.isLoading ||
    dashboardDataQuery.isFetching;

  return {
    kpisData,
    lastUpdated: dashboardDataQuery.data?.updated_at,
    isLoading,
    isWidgetExist: !!kpisWidget,
  };
}

export interface UseReportsOverviewResult {
  revenueData?: DashboardWidgetData;
  expensesData?: DashboardWidgetData;
  lastUpdated?: string | null;
  isLoading: boolean;
  isKPIWidgetExist: boolean;
}

export interface UseExpensesDetailsResult {
  expensesDetailsData?: DashboardWidgetData;
  isLoading: boolean;
  isWidgetExist: boolean;
}

export interface UseRevenueTrendResult {
  revenueTrendData?: DashboardWidgetData;
  isLoading: boolean;
  isWidgetExist: boolean;
}

export function useExpensesDetails(
  filters: DashboardDataFilters,
): UseExpensesDetailsResult {
  const {
    dashboardId,
    dashboard,
    isLoading: isDashboardLoading,
  } = useReportsDashboard();

  const expensesDetailsWidget = dashboard?.widgets?.find(
    (widget) => widget.key === DASHBOARD_WIDGET_KEYS.EXPENSE_DETAILS,
  );

  const widgetIds = compact([expensesDetailsWidget?.id]);

  const dashboardDataQuery = useCarrierDashboardData(
    dashboardId,
    filters,
    widgetIds,
  );

  const expensesDetailsData = useMemo(
    () =>
      dashboardDataQuery.data?.widgets?.find(
        (widget) => widget.id === expensesDetailsWidget?.id,
      ),
    [dashboardDataQuery.data, expensesDetailsWidget?.id],
  );

  const isLoading =
    isDashboardLoading ||
    dashboardDataQuery.isLoading ||
    dashboardDataQuery.isFetching;

  return {
    expensesDetailsData,
    isLoading,
    isWidgetExist: !!expensesDetailsWidget,
  };
}

export function useReportsOverview(
  filters: DashboardDataFilters,
): UseReportsOverviewResult {
  const {
    dashboardId,
    dashboard,
    isLoading: isDashboardLoading,
  } = useReportsDashboard();

  const revenueWidget = dashboard?.widgets?.find(
    (widget) => widget.key === DASHBOARD_WIDGET_KEYS.REVENUE,
  );
  const expensesWidget = dashboard?.widgets?.find(
    (widget) => widget.key === DASHBOARD_WIDGET_KEYS.EXPENSES,
  );
  const isKPIWidgetExist = !!dashboard?.widgets?.find(
    (widget) => widget.kind === WIDGET_KINDS.KPI_KIND,
  );

  const widgetIds = compact([revenueWidget?.id, expensesWidget?.id]);

  const dashboardDataQuery = useCarrierDashboardData(
    dashboardId,
    filters,
    widgetIds,
  );

  const revenueData = useMemo(
    () =>
      dashboardDataQuery.data?.widgets?.find(
        (widget) => widget.id === revenueWidget?.id,
      ),
    [dashboardDataQuery.data, revenueWidget?.id],
  );

  const expensesData = useMemo(
    () =>
      dashboardDataQuery.data?.widgets?.find(
        (widget) => widget.id === expensesWidget?.id,
      ),
    [dashboardDataQuery.data, expensesWidget?.id],
  );

  const isLoading =
    isDashboardLoading ||
    dashboardDataQuery.isLoading ||
    dashboardDataQuery.isFetching;

  return {
    revenueData,
    expensesData,
    lastUpdated: dashboardDataQuery.data?.updated_at,
    isLoading,
    isKPIWidgetExist,
  };
}

export function useRevenueTrend(
  filters: DashboardDataFilters,
): UseRevenueTrendResult {
  const {
    dashboardId,
    dashboard,
    isLoading: isDashboardLoading,
  } = useReportsDashboard(DASHBOARD_KEYS.REVENUE);

  const revenueTrendWidget = dashboard?.widgets?.find(
    (widget) => widget.key === DASHBOARD_WIDGET_KEYS.REVENUE_TREND,
  );

  const widgetIds = compact([revenueTrendWidget?.id]);

  const dashboardDataQuery = useCarrierDashboardData(
    dashboardId,
    filters,
    widgetIds,
  );

  const revenueTrendData = useMemo(
    () =>
      dashboardDataQuery.data?.widgets?.find(
        (widget) => widget.id === revenueTrendWidget?.id,
      ),
    [dashboardDataQuery.data, revenueTrendWidget?.id],
  );

  const isLoading =
    isDashboardLoading ||
    dashboardDataQuery.isLoading ||
    dashboardDataQuery.isFetching;

  return {
    revenueTrendData,
    isLoading,
    isWidgetExist: !!revenueTrendWidget,
  };
}

export interface UseExpensesTrendResult {
  expensesTrendData?: DashboardWidgetData;
  isLoading: boolean;
  isWidgetExist: boolean;
}

export function useExpensesTrend(
  filters: DashboardDataFilters,
): UseExpensesTrendResult {
  const {
    dashboardId,
    dashboard,
    isLoading: isDashboardLoading,
  } = useReportsDashboard(DASHBOARD_KEYS.EXPENSES);

  const expensesTrendWidget = dashboard?.widgets?.find(
    (widget) => widget.key === DASHBOARD_WIDGET_KEYS.EXPENSES_TREND,
  );

  const widgetIds = compact([expensesTrendWidget?.id]);

  const dashboardDataQuery = useCarrierDashboardData(
    dashboardId,
    filters,
    widgetIds,
  );

  const expensesTrendData = useMemo(
    () =>
      dashboardDataQuery.data?.widgets?.find(
        (widget) => widget.id === expensesTrendWidget?.id,
      ),
    [dashboardDataQuery.data, expensesTrendWidget?.id],
  );

  const isLoading =
    isDashboardLoading ||
    dashboardDataQuery.isLoading ||
    dashboardDataQuery.isFetching;

  return {
    expensesTrendData,
    isLoading,
    isWidgetExist: !!expensesTrendWidget,
  };
}
